import { Component } from '@theme/component';

/**
 * Share Button Component
 *
 * Copies the product URL to clipboard and shows a confirmation dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} shareButton - The share button element.
 *
 * @extends {Component<Refs>}
 */
class ShareButtonComponent extends Component {
  /**
   * Handles the share button click.
   * Copies the URL to clipboard and opens the confirmation dialog.
   */
  handleShare = async () => {
    const shareUrl = this.dataset.shareUrl;
    const dialogId = this.dataset.dialogId;

    if (!shareUrl) {
      console.warn('Share URL not provided');
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);

      // Open the confirmation dialog
      if (dialogId) {
        const dialogElement = document.getElementById(dialogId);
        if (dialogElement && typeof dialogElement.showDialog === 'function') {
          dialogElement.showDialog();
        }
      }
    } catch (error) {
      console.error('Failed to copy URL:', error);

      // Fallback for older browsers
      this.#fallbackCopyToClipboard(shareUrl, dialogId);
    }
  };

  /**
   * Fallback method for copying to clipboard in older browsers.
   *
   * @param {string} text - The text to copy.
   * @param {string | undefined} dialogId - The dialog element ID.
   */
  #fallbackCopyToClipboard(text, dialogId) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');

      // Open the confirmation dialog
      if (dialogId) {
        const dialogElement = document.getElementById(dialogId);
        if (dialogElement && typeof dialogElement.showDialog === 'function') {
          dialogElement.showDialog();
        }
      }
    } catch (error) {
      console.error('Fallback copy failed:', error);
    }

    document.body.removeChild(textArea);
  }
}

if (!customElements.get('share-button-component')) {
  customElements.define('share-button-component', ShareButtonComponent);
}
